import { useMemo, useState } from 'react';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

// Accepts Date, moment/dayjs, epoch ms or a date string; returns epoch ms or NaN.
const toEpochMs = (value) => {
  if (value === undefined || value === null || value === '') return NaN;
  const raw = typeof value?.valueOf === 'function' ? value.valueOf() : value;
  return new Date(raw).getTime();
};

const floorToMinute = (ms) => Math.floor(ms / MINUTE_MS) * MINUTE_MS;
const ceilToMinuteEnd = (ms) => floorToMinute(ms) + MINUTE_MS - 1;

/**
 * Resolves a request time window that stays identical across re-renders, so it can safely be part of
 * a query key. Values are rounded to the minute (start to its first ms, end to its last ms), so a
 * caller can pass `new Date()` / `moment()` on every render, and re-opening a view within the same
 * minute is served from the cache.
 *
 * Either pass explicit `startDate`/`endDate`, or `days` to look back from `endDate` (default: now,
 * fixed when the component mounts).
 *
 * @param {{ startDate?: *, endDate?: *, days?: number }} window
 * @returns {{ startDate?: string, endDate?: string }} ISO strings; empty when no start can be resolved.
 */
export const useStableTimeWindow = ({ startDate, endDate, days } = {}) => {
  // "Now" is captured once per mount, so the window (and query key) does not drift on re-renders.
  const [mountedAt] = useState(() => Date.now());
  const startMinute = floorToMinute(toEpochMs(startDate));
  const endMinute = floorToMinute(toEpochMs(endDate));
  const lookbackDays = Number(days);

  return useMemo(() => {
    const end = ceilToMinuteEnd(Number.isFinite(endMinute) ? endMinute : mountedAt);
    let start = startMinute;
    if (!Number.isFinite(start) && Number.isFinite(lookbackDays) && lookbackDays > 0) {
      start = end - lookbackDays * DAY_MS;
    }
    if (!Number.isFinite(start)) return {};
    return { startDate: new Date(start).toISOString(), endDate: new Date(end).toISOString() };
  }, [startMinute, endMinute, lookbackDays, mountedAt]);
};
